<template>
  <NavigationMenuItem
    v-if="!hidden"
    class="group inline-flex items-center transition duration-300 hover:cursor-pointer relative"
    :class="isMobile ? 'flex-col py-1 px-1.5' : 'flex grow'"
  >
    <Component
      :is="showTooltip ? Tooltip : Primitive"
      :title="title"
      disableHoverableContent
      delay="0"
      side="right"
      class="w-full"
    >
      <Component
        :is="isLink || modal ? Primitive : Drawer"
        v-bind="
          isLink || modal
            ? { asChild: true }
            : { variant: 'nav', background: 'white' }
        "
      >
        <Component
          :is="isLink ? NavigationMenuLink : NavigationMenuTrigger"
          asChild
        >
          <Component
            :is="inDrawer && !external ? DrawerClose : Primitive"
            asChild
          >
            <Component
              :is="isLink ? (external ? 'a' : Link) : 'div'"
              class="transition duration-300 flex items-center px-3 gap-2 group-data-[collapsed=true]/nav:md:px-3 group-data-[collapsed=true]/nav:md:py-3 group-data-[collapsed=true]/nav:md:h-11 group-data-[collapsed=true]/nav:md:w-11"
              :class="{
                'w-14 h-14 pt-4.5 pb-3.5': isMobile,
                'h-11 grow py-2 rounded-xl': !isMobile,
                'bg-alpha-white-10':
                  viewportMode === 'desktop' &&
                  active &&
                  variant === 'on-brand',
                'md:group-hover:bg-alpha-black-10':
                  !(viewportMode === 'desktop' && active) &&
                  variant === 'on-brand',
                'bg-utility-brand-50_alt':
                  viewportMode === 'desktop' && active && variant === 'white',
                'md:group-hover:bg-utility-brand-50_alt':
                  !(viewportMode === 'desktop' && active) &&
                  variant === 'white',
              }"
              v-bind="
                isLink ? { href, target: external ? '_blank' : undefined } : {}
              "
              :data-id="trackingId"
            >
              <div
                class="transition duration-300 flex items-center grow group-data-[collapsed=true]/nav:md:justify-center"
                :class="[
                  isMobile ? 'flex-col justify-center gap-0.5' : 'gap-3',
                ]"
              >
                <Icon
                  class="shrink-0"
                  :size="isMobile ? '2xl' : 'xl'"
                  :class="{
                    'text-alpha-white-100': active && variant === 'on-brand',
                    'text-alpha-white-80 md:group-hover:text-alpha-white-100':
                      !active && variant === 'on-brand',
                    'fg-brand-primary': active && variant === 'white',
                    'text-alpha-black-80 md:group-hover:text-alpha-black-100':
                      !active && variant === 'white',
                  }"
                  :name="icon"
                  :variant="active ? 'fill' : 'outline'"
                />
                <span
                  class="group-data-[collapsed=true]/nav:md:hidden"
                  :class="{
                    'text-[10px] leading-xs font-semibold': isMobile,
                    'text-style-sm leading-sm font-medium': !isMobile,
                    'text-alpha-white-100': active && variant === 'on-brand',
                    'text-alpha-white-80 md:group-hover:text-alpha-white-100':
                      !active && variant === 'on-brand',
                    'text-alpha-black-100': active && variant === 'white',
                    'text-alpha-black-80 md:group-hover:text-alpha-black-100':
                      !active && variant === 'white',
                  }"
                >
                  {{ title }}
                </span>
                <Dot
                  v-if="!isMobile && count"
                  class="ml-auto w-2 h-2 group-data-[collapsed=false]/nav:hidden md:absolute right-2.5 top-2.5 fg-brand-primary"
                />
              </div>
              <Badge
                v-if="!isMobile && count"
                :label="count"
                type="modern"
                size="sm"
                class="transition duration-300 px-0 border-0 aspect-square justify-center group-data-[collapsed=true]/nav:md:hidden"
                :class="{
                  '!group-hover:bg-alpha-black-30 !text-alpha-white-100':
                    variant === 'on-brand',
                  '!bg-alpha-black-30': active && variant === 'on-brand',
                  '!bg-alpha-black-20': !active && variant === 'on-brand',
                  '!bg-utility-brand-100 !text-utility-brand-700':
                    variant === 'white',
                }"
              />
              <Icon
                v-if="!isMobile && external"
                name="link-external"
                class="shrink-0 group-data-[collapsed=true]/nav:md:hidden"
                :class="{
                  'text-alpha-white-80 md:group-hover:text-alpha-white-100':
                    variant === 'on-brand',
                  'text-alpha-black-80 md:group-hover:text-alpha-black-100':
                    variant === 'white',
                }"
                size="sm"
              />
            </Component>
          </Component>
        </Component>
        <template #content>
          <slot />
        </template>
      </Component>
    </Component>
  </NavigationMenuItem>
</template>

<script setup>
// Import Vue Utilites
import { computed } from "vue";

// Import Components
import { Link } from "@inertiajs/vue3";
import {
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
  Primitive,
} from "radix-vue";
import { Drawer, DrawerClose } from "@/SharedComponents/Drawer";
import { Badge } from "@/SharedComponents/Badges";
import { Icon } from "@/SharedComponents/Icon";
import { Tooltip } from "@/SharedComponents/Tooltips";
import { Dot } from "@/SharedComponents/Dots";

// Emits
defineEmits(["open"]);

// Define Props
const props = defineProps({
  icon: { type: String, required: true },
  title: { type: String, required: true },
  href: { type: String, required: false },
  active: { type: Boolean, required: false },
  count: { type: [String, Number], required: false },
  external: { type: Boolean, required: false },
  modal: { type: Boolean, required: false },
  variant: { type: String, default: "on-brand" },
  viewport: {
    type: String,
    validator: (val) => ["mobile", "desktop", "reactive"].includes(val),
    default: "reactive",
  },
  trackingId: { type: String, required: false },
  hidden: { type: Boolean, required: false },
  showTooltip: { type: Boolean, required: false },
});

// Composables
import { useBreakpoints } from "@/Composables/useBreakpoints";

// Breakpoints
const { maxSmall } = useBreakpoints();

// Computed Props
const isLink = computed(() => props.href !== undefined);
const isMobile = computed(() => viewportMode.value === "mobile");
const inDrawer = computed(() => maxSmall.value && !isMobile.value);

const viewportMode = computed(() => {
  if (props.viewport === "reactive") {
    return maxSmall.value ? "mobile" : "desktop";
  }
  return props.viewport;
});
</script>
